/**
 * Phase 18 — Centralized Three-Way Matching Engine
 * Reconciles Purchase Order commercial terms, Goods Receipt accepted stock, and Invoice line claims.
 * Identifies quantity/price variances, missing GRNs, vendor/currency mismatches, and overbilling.
 */

import {
  ThreeWayMatchStatus,
  InvoiceException,
  InvoiceItemRecord,
} from '@/types/domain/invoice-management';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { GoodsReceiptRecord } from '@/types/domain/grn-management';
import { roundCurrency, reconcileInvoiceTotals } from '@/features/invoices/utils/invoiceCalculations';

export interface MatchingPolicyConfig {
  priceVarianceTolerancePercent: number; // e.g. 0.5% (Implementation Choice)
  priceVarianceToleranceAmount: number; // e.g. $1.00 (Implementation Choice)
  allowPartialDeliveryMatching: boolean;
  requireGrnForGoods: boolean;
}

export const DEFAULT_MATCHING_POLICY: MatchingPolicyConfig = {
  priceVarianceTolerancePercent: 0.5,
  priceVarianceToleranceAmount: 1.0,
  allowPartialDeliveryMatching: true,
  requireGrnForGoods: true,
};

export interface ThreeWayMatchResult {
  matchingStatus: ThreeWayMatchStatus;
  exceptions: InvoiceException[];
  evaluatedItems: InvoiceItemRecord[];
  isPassed: boolean;
  totalVarianceAmount: number;
}

export function executeThreeWayMatch(params: {
  invoiceId: string;
  invoiceNumber: string;
  vendorId: string;
  vendorName: string;
  currency: string;
  subtotal: number;
  taxAmount: number;
  shippingAmount: number;
  otherCharges: number;
  discountAmount: number;
  totalAmount: number;
  po?: PurchaseOrderRecord | null;
  grn?: GoodsReceiptRecord | null;
  rawItems: Array<{
    poItemId: string;
    lineNumber: number;
    materialCode: string;
    description: string;
    quantity: number;
    uom: string;
    unitPrice: number;
    taxRate: number;
    discount?: number;
  }>;
  policy?: MatchingPolicyConfig;
}): ThreeWayMatchResult {
  const {
    invoiceId,
    vendorId,
    currency,
    subtotal,
    taxAmount,
    shippingAmount,
    otherCharges,
    discountAmount,
    totalAmount,
    po,
    grn,
    rawItems,
    policy = DEFAULT_MATCHING_POLICY,
  } = params;

  const exceptions: InvoiceException[] = [];
  let totalVarianceAmount = 0;
  const now = new Date().toISOString();

  // 1. PO Existence & Status Validation
  if (!po) {
    exceptions.push({
      id: `ex-${invoiceId}-missing-po`,
      invoiceId,
      code: 'MISSING_PO',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: 'Referenced Purchase Order does not exist in the active procurement repository.',
      expectedValue: 'Active PO Reference',
      actualValue: 'None',
      detectedAt: now,
    });

    return {
      matchingStatus: 'EXCEPTION',
      exceptions,
      evaluatedItems: [],
      isPassed: false,
      totalVarianceAmount: 0,
    };
  }

  if (po.status === 'CANCELLED') {
    exceptions.push({
      id: `ex-${invoiceId}-po-cancelled`,
      invoiceId,
      code: 'CANCELLED_PO',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Purchase Order #${po.poNumber} has been cancelled and cannot accept invoice billing.`,
      expectedValue: 'Active PO',
      actualValue: 'CANCELLED',
      detectedAt: now,
    });
  }

  // 2. Vendor Mismatch Validation
  if (po.vendorId !== vendorId) {
    exceptions.push({
      id: `ex-${invoiceId}-vendor-mismatch`,
      invoiceId,
      code: 'VENDOR_MISMATCH',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Invoice vendor (${vendorId}) does not match authorized PO vendor (${po.vendorId} - ${po.vendorName}).`,
      expectedValue: po.vendorName,
      actualValue: params.vendorName,
      detectedAt: now,
    });
  }

  // 3. Currency Mismatch Validation
  if (po.currency !== currency) {
    exceptions.push({
      id: `ex-${invoiceId}-currency-mismatch`,
      invoiceId,
      code: 'CURRENCY_MISMATCH',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Invoice currency (${currency}) does not match authorized PO contract currency (${po.currency}).`,
      expectedValue: po.currency,
      actualValue: currency,
      detectedAt: now,
    });
  }

  // 4. Missing GRN Validation
  if (policy.requireGrnForGoods && !grn) {
    exceptions.push({
      id: `ex-${invoiceId}-missing-grn`,
      invoiceId,
      code: 'MISSING_GRN',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Physical goods receipt evidence missing for PO #${po.poNumber}. Payment blocked until GRN is issued.`,
      expectedValue: 'Approved Goods Receipt (GRN)',
      actualValue: 'No GRN Found',
      detectedAt: now,
    });
  }

  // 5. Line Items Evaluation (PO vs GRN Accepted vs Billed)
  let hasQuantityVariance = false;
  let hasPriceVariance = false;
  let hasOverbilling = false;

  const evaluatedItems: InvoiceItemRecord[] = rawItems.map((item, idx) => {
    // Find matching PO item
    const poItem = po.items.find(
      (p) => p.id === item.poItemId || p.materialCode === item.materialCode || p.lineNumber === item.lineNumber
    );

    // Find matching GRN item
    const grnItem = grn?.items.find(
      (g) => g.poItemId === item.poItemId || g.materialCode === item.materialCode || g.lineNumber === item.lineNumber
    );

    const poQuantity = poItem?.quantityOrdered ?? 0;
    const poUnitPrice = poItem?.unitPrice ?? 0;
    // CRITICAL: Must use acceptedQuantity, never gross physical received
    const grnAcceptedQuantity = grnItem?.acceptedQuantity ?? (grn ? 0 : poQuantity);

    const billedQty = item.quantity;
    const billedPrice = item.unitPrice;

    const varianceQty = billedQty - grnAcceptedQuantity;
    const priceDiff = billedPrice - poUnitPrice;
    const varianceAmt = roundCurrency(priceDiff * billedQty);

    totalVarianceAmount += Math.abs(varianceAmt);

    let itemMatchStatus: InvoiceItemRecord['matchingStatus'] = 'MATCHED';

    // Quantity checks
    if (!grn) {
      itemMatchStatus = 'MISSING_GRN';
    } else if (billedQty > poQuantity) {
      itemMatchStatus = 'OVERBILLING';
      hasOverbilling = true;
      exceptions.push({
        id: `ex-${invoiceId}-overbill-${idx}`,
        invoiceId,
        code: 'OVERBILLING',
        severity: 'BLOCKING',
        status: 'OPEN',
        description: `Line ${item.lineNumber} (${item.materialCode}): Invoiced quantity (${billedQty}) exceeds authorized PO quantity (${poQuantity}).`,
        expectedValue: poQuantity,
        actualValue: billedQty,
        detectedAt: now,
      });
    } else if (varianceQty !== 0) {
      itemMatchStatus = 'QUANTITY_VARIANCE';
      hasQuantityVariance = true;
      exceptions.push({
        id: `ex-${invoiceId}-qty-var-${idx}`,
        invoiceId,
        code: 'QUANTITY_VARIANCE',
        severity: 'WARNING',
        status: 'OPEN',
        description: `Line ${item.lineNumber} (${item.materialCode}): Invoiced quantity (${billedQty}) differs from warehouse accepted quantity (${grnAcceptedQuantity}).`,
        expectedValue: `${grnAcceptedQuantity} ${item.uom}`,
        actualValue: `${billedQty} ${item.uom}`,
        detectedAt: now,
      });
    }

    // Price checks
    const priceVariancePercent = poUnitPrice > 0 ? (priceDiff / poUnitPrice) * 100 : 0;
    const exceedsPriceTolerance =
      priceDiff > policy.priceVarianceToleranceAmount &&
      priceVariancePercent > policy.priceVarianceTolerancePercent;

    if (exceedsPriceTolerance) {
      itemMatchStatus = 'PRICE_VARIANCE';
      hasPriceVariance = true;
      exceptions.push({
        id: `ex-${invoiceId}-price-var-${idx}`,
        invoiceId,
        code: 'PRICE_VARIANCE',
        severity: 'BLOCKING',
        status: 'OPEN',
        description: `Line ${item.lineNumber} (${item.materialCode}): Invoiced price (${billedPrice}) exceeds PO contract price (${poUnitPrice}) by ${priceVariancePercent.toFixed(1)}%.`,
        expectedValue: poUnitPrice,
        actualValue: billedPrice,
        detectedAt: now,
      });
    }

    const lineTotal = roundCurrency(billedQty * billedPrice * (1 + item.taxRate / 100));

    return {
      id: `inv-item-${invoiceId}-${idx + 1}`,
      invoiceId,
      poItemId: item.poItemId,
      lineNumber: item.lineNumber,
      materialCode: item.materialCode,
      description: item.description,
      quantity: billedQty,
      poQuantity,
      grnQuantity: grnAcceptedQuantity,
      uom: item.uom,
      unitPrice: billedPrice,
      poUnitPrice,
      taxRate: item.taxRate,
      taxAmount: roundCurrency(billedQty * billedPrice * (item.taxRate / 100)),
      discount: item.discount || 0,
      lineTotal,
      grnReference: grn?.grnNumber,
      matchingStatus: itemMatchStatus,
      varianceQuantity: varianceQty,
      varianceAmount: varianceAmt,
    };
  });

  // 6. Commercial Totals Reconcilation
  const recon = reconcileInvoiceTotals(subtotal, taxAmount, shippingAmount, otherCharges, discountAmount, totalAmount);
  if (!recon.isReconciled) {
    exceptions.push({
      id: `ex-${invoiceId}-total-mismatch`,
      invoiceId,
      code: 'TOTAL_MISMATCH',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Invoice declared total (${totalAmount}) does not reconcile with computed line total + taxes + charges (${recon.expectedTotal}). Difference: ${recon.difference}.`,
      expectedValue: recon.expectedTotal,
      actualValue: totalAmount,
      detectedAt: now,
    });
  }

  // Derive Overall Matching Status
  let matchingStatus: ThreeWayMatchStatus = 'MATCHED';
  const hasBlockingExceptions = exceptions.some((e) => e.severity === 'BLOCKING' || e.severity === 'CRITICAL');

  if (!grn && policy.requireGrnForGoods) {
    matchingStatus = 'MISSING_GRN';
  } else if (hasOverbilling) {
    matchingStatus = 'OVERBILLING';
  } else if (hasPriceVariance) {
    matchingStatus = 'PRICE_VARIANCE';
  } else if (hasQuantityVariance) {
    matchingStatus = 'QUANTITY_VARIANCE';
  } else if (hasBlockingExceptions) {
    matchingStatus = 'EXCEPTION';
  }

  const isPassed = matchingStatus === 'MATCHED' && !hasBlockingExceptions;

  return {
    matchingStatus,
    exceptions,
    evaluatedItems,
    isPassed,
    totalVarianceAmount: roundCurrency(totalVarianceAmount),
  };
}
